import { useState } from "react";
import { Download, FileSpreadsheet, FileText, Image as ImageIcon, Paperclip, X } from "lucide-react";
import { Text } from "@/components/Typography";
import { useToast } from "@/hooks/useToast";
import { resourcesApi } from "@/services/api";
import type { ProgramResource } from "@/services/api";
import { formatFileSize, getFileExtension } from "@/utils/documentValidation";

const iconFor = (filename: string) => {
  const ext = getFileExtension(filename);
  if (ext === "xlsx") return FileSpreadsheet;
  if (ext === "png" || ext === "jpg" || ext === "jpeg") return ImageIcon;
  return FileText;
};

/**
 * Attachment chips rendered inside a message bubble.
 *
 * The download link is fetched at click time, never stored on the message:
 * presigned URLs expire in minutes, so embedding one in a message that lives in
 * history forever would hand out dead links. This is why the API returns
 * attachment metadata without a URL.
 */
export const MessageAttachments = ({
  attachments,
  programId,
}: {
  attachments: ProgramResource[];
  /** Programme the conversation belongs to — the resource endpoints are scoped by it. */
  programId: number | null | undefined;
}) => {
  const { showErrorToast } = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  if (!attachments?.length) return null;

  const handleDownload = async (resource: ProgramResource) => {
    // programId comes from the conversation; without it we cannot address the
    // resource endpoints at all, so fail loudly rather than silently no-op.
    if (!programId) {
      showErrorToast("This conversation isn't linked to a program.", "Download failed");
      return;
    }
    setBusyId(resource.id);
    try {
      const { url } = await resourcesApi.getDownloadLink(programId, resource.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        "Could not open that file.";
      showErrorToast(detail, "Download failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mt-2 flex flex-col gap-1.5">
      {attachments.map((a) => {
        const Icon = iconFor(a.filename);
        return (
          <button
            key={a.id}
            type="button"
            onClick={() => void handleDownload(a)}
            disabled={busyId === a.id}
            title={`Download ${a.filename}`}
            /* Tinted RELATIVE to the bubble rather than with a surface token.
               Both bubble variants are light in light mode and dark in dark
               mode (--theme-msg-bubble-*-bg), so a translucent black/white
               overlay reads correctly on all four combinations.

               The previous version used bg-surface-card, which is a near-black
               panel colour unrelated to the messaging palette — against the
               dark navy received bubble (#1E3558) it rendered as an almost
               invisible black block. */
            className="flex items-center gap-2.5 rounded-lg border border-black/10 bg-black/[0.04] px-2.5 py-2 text-left transition-colors hover:bg-black/[0.08] disabled:opacity-60 dark:border-white/20 dark:bg-white/[0.08] dark:hover:bg-white/[0.14]"
          >
            {/* Text colours are explicit and match the message body
                (text-theme-text), so the filename is as legible as the message
                it arrived with. Relying on inheritance left it washed out. */}
            <Icon size={15} className="flex-shrink-0 text-theme-text opacity-70" />
            <span className="min-w-0 flex-1">
              <Text
                as="span"
                className="block truncate font-inter text-[12px] font-medium text-theme-text"
              >
                {a.filename}
              </Text>
              {a.sizeBytes != null && (
                <Text as="span" className="block font-inter text-[10px] text-theme-text-muted">
                  {formatFileSize(a.sizeBytes)}
                </Text>
              )}
            </span>
            <Download size={13} className="flex-shrink-0 text-theme-text opacity-60" />
          </button>
        );
      })}
    </div>
  );
};

/**
 * The staged-files strip shown above the composer before sending.
 *
 * Files are uploaded as soon as they are picked, so each chip here already
 * exists as a programme resource. Removing one only drops it from the outgoing
 * message — the file stays in the Resources tab, which is intentional: it was
 * uploaded to the programme, and every uploaded file belongs there.
 */
export const PendingAttachments = ({
  pending,
  uploading,
  onRemove,
}: {
  pending: ProgramResource[];
  uploading: boolean;
  onRemove: (id: number) => void;
}) => {
  if (!pending.length && !uploading) return null;

  return (
    <div className="mb-2 flex flex-wrap items-center gap-2">
      {pending.map((a) => (
        <span
          key={a.id}
          /* Messaging-theme tokens, matching the composer's own textarea
             (bg-theme-bg-surface-raised / border-theme-border) rather than the
             surface-* family used by the programs pages. */
          className="flex items-center gap-1.5 rounded-full border border-theme-border bg-theme-bg-surface-raised px-2.5 py-1"
        >
          <Paperclip size={11} className="flex-shrink-0 text-theme-text-muted" />
          <Text as="span" className="max-w-[160px] truncate font-inter text-[11px] text-theme-text">
            {a.filename}
          </Text>
          <button
            type="button"
            aria-label={`Remove ${a.filename}`}
            onClick={() => onRemove(a.id)}
            className="text-theme-text-muted hover:text-theme-text-heading"
          >
            <X size={11} />
          </button>
        </span>
      ))}
      {uploading && (
        <Text as="span" className="font-inter text-[11px] text-theme-text-muted">
          Uploading…
        </Text>
      )}
    </div>
  );
};
